/**
 * Thin helper to POST to our FastAPI backend email relay.
 * The frontend calls Supabase RPCs directly for everything else.
 */
import { supabase } from "@/lib/supabaseClient";
import {
  getStoredImpersonationAccessToken,
  getStoredImpersonationSessionId,
} from "@/lib/impersonationContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const APPOINTMENT_CONFIRMATION_DEV_BACKEND_URL = process.env.REACT_APP_APPOINTMENT_CONFIRMATION_DEV_BACKEND_URL;
const LOCAL_BACKEND_URL = "http://127.0.0.1:8001";

function getBackendUrl() {
  if (BACKEND_URL) return BACKEND_URL.replace(/\/+$/, "");
  if (typeof window !== "undefined") {
    const host = window.location?.hostname;
    if (host === "localhost" || host === "127.0.0.1" || host === "::1") {
      return LOCAL_BACKEND_URL;
    }
  }
  return "";
}

async function requestBackend(path, {
  method = "GET",
  payload,
  formData,
  timeoutMs = 90_000,
  backendUrlOverride,
} = {}) {
  const auth = await getBackendAuthContext();
  const token = auth.token;
  if (!token) return { ok: false, code: "no_session", message: "You are not signed in." };
  const backendUrl = backendUrlOverride || getBackendUrl();
  if (!backendUrl) {
    return {
      ok: false,
      code: "no_backend",
      message: "Backend isn't configured (REACT_APP_BACKEND_URL is empty).",
    };
  }

  // 90s client-side timeout via AbortController — slightly under the
  // Vercel Pro serverless cap (60s on Pro, 300s with longer config)
  // so a stuck call surfaces as a clean error here instead of the
  // edge dropping the request.
  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), timeoutMs);
  let r;
  const headers = {
    "Authorization": `Bearer ${token}`,
  };
  if (auth.impersonationSessionId) {
    headers["x-impersonation-session-id"] = auth.impersonationSessionId;
  }
  let requestBody;
  if (formData) {
    requestBody = formData;
  } else if (payload !== undefined) {
    headers["Content-Type"] = "application/json";
    requestBody = JSON.stringify(payload);
  }
  try {
    r = await fetch(`${backendUrl}${path}`, {
      method,
      headers,
      body: requestBody,
      signal: ac.signal,
    });
  } catch (err) {
    clearTimeout(timer);
    if (isRequestCloneError(err) && payload !== undefined && !formData && method === "POST") {
      return xhrPostJson(`${backendUrl}${path}`, payload, auth);
    }
    // Network-level failure — DNS, CORS preflight, backend down,
    // offline, or our 90s timeout. Returning a structured error
    // keeps the UI calm; the caller (Tx Plan propose flow, etc.)
    // falls back to the PDF download path.
    if (err?.name === "AbortError") {
      return {
        ok: false,
        code: "timeout",
        message: "Backend is taking too long. Try again.",
      };
    }
    return {
      ok: false,
      code: "network_error",
      message: `Could not reach the backend (${err?.message || "fetch failed"}).`,
    };
  }
  clearTimeout(timer);
  let responseBody = null;
  try { responseBody = await r.json(); } catch { /* non-JSON */ }
  if (!r.ok) {
    return normalizeHttpError(r.status, responseBody);
  }
  return responseBody ?? { ok: false, code: "empty_response", message: "Empty response." };
}

async function postJson(path, payload) {
  return requestBackend(path, { method: "POST", payload });
}

async function downloadBackendFile(path, filename, { timeoutMs = 90_000 } = {}) {
  const auth = await getBackendAuthContext();
  const token = auth.token;
  if (!token) return { ok: false, code: "no_session", message: "You are not signed in." };
  const backendUrl = getBackendUrl();
  if (!backendUrl) {
    return {
      ok: false,
      code: "no_backend",
      message: "Backend isn't configured (REACT_APP_BACKEND_URL is empty).",
    };
  }

  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), timeoutMs);
  let response;
  try {
    response = await fetch(`${backendUrl}${path}`, {
      method: "GET",
      headers: buildBackendAuthHeaders(auth),
      signal: ac.signal,
    });
  } catch (err) {
    clearTimeout(timer);
    return {
      ok: false,
      code: err?.name === "AbortError" ? "timeout" : "network_error",
      message: err?.name === "AbortError" ? "Backend is taking too long. Try again." : `Could not reach the backend (${err?.message || "fetch failed"}).`,
    };
  }
  clearTimeout(timer);

  if (!response.ok) {
    let body = null;
    try { body = await response.json(); } catch { /* non-JSON */ }
    return normalizeHttpError(response.status, body);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  return { ok: true };
}

function isRequestCloneError(err) {
  const message = String(err?.message || "");
  return err?.name === "DataCloneError"
    || (message.includes("postMessage") && message.includes("could not be cloned"));
}

async function getBackendAuthContext() {
  const impersonationSessionId = getStoredImpersonationSessionId();
  const impersonationToken = impersonationSessionId ? getStoredImpersonationAccessToken() : null;
  if (impersonationToken) {
    return { token: impersonationToken, impersonationSessionId };
  }
  const { data: sess } = await supabase.auth.getSession();
  return { token: sess?.session?.access_token || null, impersonationSessionId: null };
}

function buildBackendAuthHeaders(auth) {
  const headers = {
    Authorization: `Bearer ${auth.token}`,
  };
  if (auth.impersonationSessionId) {
    headers["x-impersonation-session-id"] = auth.impersonationSessionId;
  }
  return headers;
}

function xhrPostJson(url, payload, auth) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url, true);
    xhr.timeout = 90_000;
    xhr.setRequestHeader("Content-Type", "application/json");
    xhr.setRequestHeader("Authorization", `Bearer ${auth.token}`);
    if (auth.impersonationSessionId) {
      xhr.setRequestHeader("x-impersonation-session-id", auth.impersonationSessionId);
    }

    xhr.onload = () => {
      let body = null;
      try { body = JSON.parse(xhr.responseText || "null"); } catch { /* non-JSON */ }
      if (xhr.status < 200 || xhr.status >= 300) {
        resolve(normalizeHttpError(xhr.status, body));
        return;
      }
      resolve(body ?? { ok: false, code: "empty_response", message: "Empty response." });
    };

    xhr.onerror = () => resolve({
      ok: false,
      code: "network_error",
      message: "Could not reach the email backend (XHR fallback failed).",
    });
    xhr.ontimeout = () => resolve({
      ok: false,
      code: "timeout",
      message: "Email backend is taking too long (>90s). Try again or send manually.",
    });

    xhr.send(JSON.stringify(payload));
  });
}

function normalizeHttpError(status, body) {
  const message = body?.detail ?? body?.message;
  if (status === 409 && !message) {
    return {
      ok: false,
      code: "conflict",
      message: "That email is already a Dental OS user. Use Invite Member to add them to this clinic.",
    };
  }
  return {
    ok: false,
    code: body?.code ?? (status === 409 ? "conflict" : "http_error"),
    message: message ?? `HTTP ${status}`,
  };
}

export function sendInvitationEmail(payload) {
  return postJson("/api/email/send-invitation", payload);
}

export function sendTreatmentPlanEmail(payload) {
  return postJson("/api/email/send-treatment-plan", payload);
}

export function sendMedicalHistoryEmail(payload) {
  return postJson("/api/email/send-medical-history", payload);
}

export function sendPaymentLinkEmail(payload) {
  return postJson("/api/email/send-payment-link", payload);
}

export function sendCheckoutReceiptEmail(payload) {
  return postJson("/api/email/send-checkout-receipt", payload);
}

export function updateRecoveryPassword(password) {
  return postJson("/api/auth/recovery-password", { password });
}

export async function sendPublicBookingAcknowledgment(payload) {
  const backendUrl = getBackendUrl();
  if (!backendUrl) {
    return {
      ok: false,
      code: "no_backend",
      message: "Backend isn't configured (REACT_APP_BACKEND_URL is empty).",
    };
  }
  try {
    const response = await fetch(`${backendUrl}/api/public-booking/send-acknowledgement`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    let body = null;
    try { body = await response.json(); } catch { /* non-JSON */ }
    if (!response.ok) return normalizeHttpError(response.status, body);
    return body ?? { ok: false, code: "empty_response", message: "Empty response." };
  } catch (err) {
    return {
      ok: false,
      code: "network_error",
      message: `Could not reach the backend (${err?.message || "fetch failed"}).`,
    };
  }
}

export function devDispatchAppointmentConfirmationEmails({ limit = 5, overrideAppointmentIds = [] } = {}) {
  const safeLimit = Math.max(1, Math.min(Number(limit) || 5, 25));
  const backendUrlOverride = APPOINTMENT_CONFIRMATION_DEV_BACKEND_URL
    ? APPOINTMENT_CONFIRMATION_DEV_BACKEND_URL.replace(/\/+$/, "")
    : null;
  const cleanOverrideIds = Array.isArray(overrideAppointmentIds)
    ? overrideAppointmentIds.filter(Boolean).slice(0, safeLimit)
    : [];
  return requestBackend(`/api/appointment-confirmations/dev-dispatch-pending-emails?limit=${safeLimit}`, {
    method: "POST",
    payload: cleanOverrideIds.length > 0 ? { appointment_ids: cleanOverrideIds } : undefined,
    timeoutMs: 120_000,
    backendUrlOverride,
  });
}

export function acceptTrialInvitation(inviteToken) {
  return postJson("/admin/invitations/accept", { invite_token: inviteToken });
}

export function sendTenantSupportMessageEmailNotification({ clinicId, supportMessageId }) {
  return postJson("/admin/tenant-support-message-email", {
    clinic_id: clinicId,
    support_message_id: supportMessageId,
  });
}

export function downloadBillingHistoryPdf(historyId, filename) {
  return downloadBackendFile(`/api/billing/history/${encodeURIComponent(historyId)}/pdf`, filename);
}

export function downloadBillingHistoryZip(filename) {
  return downloadBackendFile("/api/billing/history.zip", filename, { timeoutMs: 180_000 });
}

// Clinic Health must read through Supabase, not FastAPI. The page is available
// to Solo clinics even when backend-only receipt tools are unavailable.
export function getClinicHealthData({ clinicId, monthKey, startDate, endDate }) {
  return supabase.rpc("get_clinic_health_data", {
    p_clinic_id: clinicId,
    p_month_key: monthKey || null,
    p_start_date: startDate || null,
    p_end_date: endDate || null,
  }).then(({ data, error }) => {
    if (error) {
      return {
        ok: false,
        code: error.code || "rpc_error",
        message: error.message || "Could not load clinic health.",
      };
    }
    return data;
  });
}

export function uploadClinicHealthReceiptImage({ clinicId, monthKey, file }) {
  const formData = new FormData();
  formData.set("clinic_id", clinicId);
  formData.set("month_key", monthKey);
  formData.set("file", file);
  return requestBackend("/api/clinic-health/receipt-image", {
    method: "POST",
    formData,
    timeoutMs: 120_000,
  });
}

export function scanClinicHealthReceipt({ clinicId, monthKey, file }) {
  const formData = new FormData();
  formData.set("clinic_id", clinicId);
  formData.set("month_key", monthKey);
  formData.set("file", file);
  return requestBackend("/api/clinic-health/scan-receipt", {
    method: "POST",
    formData,
    timeoutMs: 120_000,
  });
}

// Fire-and-forget GET to wake the Vercel Python serverless function
// before the user clicks Send. Cold starts add 5-10s on first call;
// preheating when a tab that's likely to send opens drops the
// "first send took 30s" experience to 2-3s. Errors are swallowed
// — this is a hint, not a contract.
export function prewarmEmailBackend() {
  const backendUrl = getBackendUrl();
  if (!backendUrl) return;
  try {
    fetch(`${backendUrl}/api/`, { method: "GET" }).catch(() => {});
  } catch { /* ignore */ }
}
